/**
 * レイヤーパネル
 * - 要素を z 順で表示（先頭が前面）
 * - dnd-kit でドラッグ&ドロップ順序入れ替え
 * - クリックで選択
 * - 表示／非表示・ロック切替
 */
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AdElement, AdImageSlide } from '@goenhub/shared';
import { ChevronDown, ChevronRight, Eye, EyeOff, Folder, FolderOpen, GripVertical, Image as ImageIcon, ImageDown, Lock, LockOpen, PenTool, Shapes, Trash2, Type } from 'lucide-react';

import { BACKGROUND_LAYER_ID } from './InteractiveCanvas';
import { useMemo, useState } from 'react';

interface Props {
  slide: AdImageSlide;
  selectedId: string | null;
  /** Phase 38.3: 選択中の全ID（複数選択時のハイライト用） */
  selectedIds?: string[];
  /** Phase 38.4: グループ編集中の groupId */
  activeGroupId?: string | null;
  onSelect: (id: string, opts?: { shift?: boolean }) => void;
  onReorder: (newOrder: string[]) => void;
  onPatchElement: (id: string, patch: Partial<AdElement>) => void;
  /** Phase 28: 要素削除 */
  onDeleteElement?: (id: string) => void;
  /** Phase 38.4: グループ操作 */
  onRenameGroup?: (groupId: string, name: string) => void;
  onUngroup?: (groupId: string) => void;
  onEnterGroup?: (groupId: string | null) => void;
}

export function LayerPanel({
  slide,
  selectedId,
  selectedIds,
  activeGroupId,
  onSelect,
  onReorder,
  onPatchElement,
  onDeleteElement,
  onRenameGroup,
  onEnterGroup,
}: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [renamingGroup, setRenamingGroup] = useState<string | null>(null);

  const order = useMemo(() => {
    const lo = slide.layerOrder?.length ? slide.layerOrder : slide.elements.map((e) => e.id);
    const set = new Set(slide.elements.map((e) => e.id));
    return lo.filter((id) => set.has(id));
  }, [slide]);

  const elementMap = useMemo(
    () => new Map(slide.elements.map((e) => [e.id, e])),
    [slide],
  );

  /**
   * Phase 38.4: グループを「折りたたみ可能なフォルダ」として表示するためのツリー構造を組み立てる。
   * 同一グループの連続区間はそのグループのフォルダ下に集約。
   * グループ外（groupId 未設定）要素は root にそのまま配置。
   */
  const tree = useMemo(() => {
    const groups = slide.groups ?? [];
    const groupMap = new Map(groups.map((g) => [g.id, g]));
    type Node =
      | { kind: 'element'; id: string }
      | { kind: 'group'; id: string; name: string; childIds: string[] };
    const nodes: Node[] = [];
    const groupNodeIndex = new Map<string, number>();
    for (const id of order) {
      const el = elementMap.get(id);
      if (!el) continue;
      const gid = el.groupId;
      if (gid && groupMap.has(gid)) {
        const idx = groupNodeIndex.get(gid);
        if (idx !== undefined) {
          const node = nodes[idx];
          if (node.kind === 'group') node.childIds.push(id);
        } else {
          const groupName = groupMap.get(gid)!.name;
          nodes.push({ kind: 'group', id: gid, name: groupName, childIds: [id] });
          groupNodeIndex.set(gid, nodes.length - 1);
        }
      } else {
        nodes.push({ kind: 'element', id });
      }
    }
    return nodes;
  }, [order, elementMap, slide.groups]);

  const idIsSelected = (id: string) =>
    selectedIds ? selectedIds.includes(id) : id === selectedId;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id as string);
    const newIndex = order.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(order, oldIndex, newIndex));
  };

  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1 px-1">
        レイヤー
      </h4>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5">
            {tree.map((node) => {
              if (node.kind === 'element') {
                const el = elementMap.get(node.id);
                if (!el) return null;
                return (
                  <LayerRow
                    key={node.id}
                    element={el}
                    selected={idIsSelected(node.id)}
                    indent={0}
                    onSelect={(opts) => onSelect(node.id, opts)}
                    onToggleVisible={() => onPatchElement(node.id, { visible: !el.visible })}
                    onToggleLock={() => onPatchElement(node.id, { locked: !el.locked })}
                    onDelete={onDeleteElement ? () => onDeleteElement(node.id) : undefined}
                  />
                );
              }
              // group node
              const collapsed = collapsedGroups.has(node.id);
              const isRenaming = renamingGroup === node.id;
              const groupSelected = node.childIds.every((cid) => idIsSelected(cid));
              return (
                <div key={node.id} className="space-y-0.5">
                  <div
                    className={`flex items-center gap-1 px-1 py-1 rounded text-xs cursor-pointer ${
                      activeGroupId === node.id
                        ? 'bg-[var(--accent)]/25'
                        : groupSelected
                        ? 'bg-[var(--accent)]/15'
                        : 'hover:bg-[var(--bg-hover)]'
                    }`}
                    onClick={(e) => {
                      // クリックで全 child を選択（shift で toggle）
                      const first = node.childIds[0];
                      if (first) onSelect(first, { shift: e.shiftKey });
                    }}
                    onDoubleClick={() => onEnterGroup?.(node.id)}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollapsedGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(node.id)) next.delete(node.id);
                          else next.add(node.id);
                          return next;
                        });
                      }}
                      className="text-[var(--text-muted)]"
                    >
                      {collapsed ? (
                        <ChevronRight className="size-3" />
                      ) : (
                        <ChevronDown className="size-3" />
                      )}
                    </button>
                    {collapsed ? (
                      <Folder className="size-3 shrink-0 text-[var(--text-muted)]" />
                    ) : (
                      <FolderOpen className="size-3 shrink-0 text-[var(--text-muted)]" />
                    )}
                    {isRenaming ? (
                      <input
                        autoFocus
                        defaultValue={node.name}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== node.name) onRenameGroup?.(node.id, v);
                          setRenamingGroup(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') setRenamingGroup(null);
                        }}
                        className="flex-1 text-xs bg-[var(--bg-input)] border border-[var(--border)] rounded-sm px-1 py-0 outline-none"
                      />
                    ) : (
                      <span
                        className="flex-1 truncate"
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setRenamingGroup(node.id);
                        }}
                      >
                        {node.name}
                      </span>
                    )}
                    <span className="text-[10px] text-[var(--text-muted)]">{node.childIds.length}</span>
                  </div>
                  {!collapsed &&
                    node.childIds.map((cid) => {
                      const el = elementMap.get(cid);
                      if (!el) return null;
                      return (
                        <LayerRow
                          key={cid}
                          element={el}
                          selected={idIsSelected(cid)}
                          indent={1}
                          onSelect={(opts) => {
                            // グループ内子要素クリックで個別選択（グループ編集モードに入る）
                            onEnterGroup?.(node.id);
                            onSelect(cid, opts);
                          }}
                          onToggleVisible={() => onPatchElement(cid, { visible: !el.visible })}
                          onToggleLock={() => onPatchElement(cid, { locked: !el.locked })}
                          onDelete={onDeleteElement ? () => onDeleteElement(cid) : undefined}
                        />
                      );
                    })}
                </div>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* 背景画像レイヤー（最下層・固定） */}
      {slide.background?.url && (
        <div
          className={`mt-1 pt-1 border-t border-[var(--border)] flex items-center gap-1 px-1 py-1 rounded text-xs cursor-pointer ${
            selectedId === BACKGROUND_LAYER_ID ? 'bg-[var(--accent)]/15' : 'hover:bg-[var(--bg-hover)]'
          }`}
          onClick={() => onSelect(BACKGROUND_LAYER_ID)}
        >
          <span className="w-3" />
          <ImageDown className="size-3 shrink-0 text-[var(--text-muted)]" />
          <span className="flex-1 truncate text-[var(--text-muted)]">背景画像</span>
        </div>
      )}
    </div>
  );
}

function LayerRow({
  element,
  selected,
  indent = 0,
  onSelect,
  onToggleVisible,
  onToggleLock,
  onDelete,
}: {
  element: AdElement;
  selected: boolean;
  indent?: number;
  onSelect: (opts?: { shift?: boolean }) => void;
  onToggleVisible: () => void;
  onToggleLock: () => void;
  onDelete?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const label =
    element.kind === 'text'
      ? element.content.slice(0, 24) || '(空テキスト)'
      : element.kind === 'vector'
      ? `ベクター: ${element.shape}`
      : element.kind === 'image_placeholder'
      ? element.label || '画像'
      : `パス (${element.points.length} 頂点)`;

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, paddingLeft: indent ? indent * 12 + 4 : undefined }}
      className={`flex items-center gap-1 px-1 py-1 rounded text-xs ${
        selected ? 'bg-[var(--accent)]/15' : 'hover:bg-[var(--bg-hover)]'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-[var(--text-muted)]"
        aria-label="drag"
      >
        <GripVertical className="size-3" />
      </button>
      <button
        type="button"
        onClick={(e) => onSelect({ shift: e.shiftKey })}
        className="flex-1 flex items-center gap-1 min-w-0 text-left"
      >
        {element.kind === 'text' ? (
          <Type className="size-3 shrink-0" />
        ) : element.kind === 'vector' ? (
          <Shapes className="size-3 shrink-0" />
        ) : element.kind === 'image_placeholder' ? (
          <ImageIcon className="size-3 shrink-0" />
        ) : (
          <PenTool className="size-3 shrink-0" />
        )}
        <span className="truncate">{label}</span>
      </button>
      <button
        type="button"
        onClick={onToggleVisible}
        className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        aria-label={element.visible ? 'hide' : 'show'}
      >
        {element.visible ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
      </button>
      <button
        type="button"
        onClick={onToggleLock}
        className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        aria-label={element.locked ? 'unlock' : 'lock'}
      >
        {element.locked ? <Lock className="size-3" /> : <LockOpen className="size-3" />}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="text-[var(--text-muted)] hover:text-[var(--accent-red)]"
          aria-label="delete"
        >
          <Trash2 className="size-3" />
        </button>
      )}
    </div>
  );
}
